import { useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { ExperienceEventFragment } from '@/features/events/fragments/ExperienceEventFragment';
import type { ExperienceEventFragment as ExperienceEventFragmentType } from '@/features/events/fragments/ExperienceEventFragment.generated';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createEventMetadataQueryKey } from '@/utils/queryKeys';
import { toEventMetadataInput } from '../lib/eventMetadataInput.lib';
import type {
  UpdateEventMetadataMutation,
  UpdateEventMetadataMutationVariables,
} from './useUpdateEventMetadata.generated';

export type EventMetadata = ExperienceEventFragmentType['metadata'];

type Args = {
  experienceId: string;
  eventId: string;
};

const query = gql`
  ${ExperienceEventFragment}
  mutation UpdateEventMetadata($input: UpdateEventMetadataInput!) {
    updateEventMetadata(input: $input) {
      ...ExperienceEvent
    }
  }
`;

// Persists an event's experience metadata via the dedicated updateEventMetadata
// API and mirrors the result into a per-(experience, event) cache so the
// Category control reflects the edit immediately — including for staged events
// that aren't yet imported and therefore aren't in any metadata-selecting cache.
// The returned event is also normalized by @normy, keeping metadata-selecting
// caches for already-imported events in sync.
export const useUpdateEventMetadata = ({ experienceId, eventId }: Args) => {
  const queryClient = useQueryClient();
  const queryKey = createEventMetadataQueryKey(experienceId, eventId);

  const { mutate, ...rest } = useMutation<
    UpdateEventMetadataMutation,
    QueryError,
    EventMetadata,
    { previous: EventMetadata | undefined }
  >({
    mutationFn: (metadata) =>
      gqlClient.request<
        UpdateEventMetadataMutation,
        UpdateEventMetadataMutationVariables
      >(query, {
        input: {
          experienceId,
          eventItemId: eventId,
          ...toEventMetadataInput(metadata),
        },
      }),
    onMutate: (metadata) => {
      const previous = queryClient.getQueryData<EventMetadata>(queryKey);
      queryClient.setQueryData<EventMetadata>(queryKey, metadata);
      return { previous };
    },
    onError: (_error, _metadata, context) => {
      queryClient.setQueryData<EventMetadata>(queryKey, context?.previous);
    },
    onSuccess: (data) => {
      queryClient.setQueryData<EventMetadata>(
        queryKey,
        data.updateEventMetadata.metadata
      );
    },
  });

  return {
    ...rest,
    updateEventMetadata: mutate,
  };
};
